
import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { LightShowTrack } from '../../_models/LightShowTrack';
import { LightShowTrackService } from '../../_services/track.service';
import { Genre } from '../../../app/_models/Genre';
import { Subject } from 'rxjs';
import { takeUntil, map, mergeMap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { FlashTypes } from '../../../../src/app/lightshow-preview/operation-types';
import { UserService } from '../../_services/user.service';
import { S3Service } from '../../_services/s3.service';
import { DialogService } from '../../_services/dialog.service';
import { Environment } from '../../../environments/environment';

@Component({
  templateUrl: './track-create-modal.component.html',
  styleUrls: ['./track-create-modal.component.css']
})
export class TrackCreateModalComponent implements OnInit, OnDestroy {
  public loading = false;

  public genres: Genre[];

  public uploadedTrackName: string = null;

  public track: LightShowTrack;
  public trackFile: File;
  public trackId = -1;

  public copyMode = false;

  public title: string;
  public artist: string;
  public notes: string;
  public url: string;
  public lightShowJson: string;
  public isTrackVisible: boolean;
  public trackGenres: Genre[];

  private ngOnDestroy$: Subject<void> = new Subject();
  public form: FormGroup;
  public isUserAdmin: boolean;

  private viewRef: ViewContainerRef;

  private _hasSiteManagement = false;
  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  constructor(
    private bsModalRef: BsModalRef,
    private trackService: LightShowTrackService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private userService: UserService,
    private s3Service: S3Service,
    private dialogService: DialogService
  ) { }

  ngOnInit() {
    this.userService.hasSinglePermission('SiteManagement').subscribe(permission => {
      this._hasSiteManagement = permission;
       if (this.copyMode) { 
          this.trackId = -1;
          this._initializeForm({
            title: this.track.title,
            artist: this.track.artist,
            lightShow: this.track.lightShow,
            notes: '',
            genres: this.track.genres,
            visible: true,
            trackUrl: this.track.trackUrl,
            duration: this.track.duration,
            reencode: false
          });
        } else if (this.track) {
          this.loading = true;
          this.trackService.getTrack(this.track.trackId)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(data => {
              this.trackId = data.trackId;
              this._initializeForm(data);
            });
        } else {
        this._initializeForm({
          title: '',
          artist: '',
          lightShow: '[]',
          notes: '',
          genres: [],
          visible: false,
          reencode: false
        });
      }
      this.loading = false;
    });
  }

  close() {
    this.bsModalRef.hide();
  }

  saveAndClose() {
    if (!this.form.valid) {
      this.form.markAsDirty();
      return;
    }
    this.loading = true;
    const trackData = {
      trackId: this.trackId,
      title: this.form.controls.title.value,
      trackUrl: this.form.controls.trackUrl.value,
      artist: this.form.controls.artist.value,
      notes: this.form.controls.notes.value,
      lightShow: this.form.controls.lightShow.value,
      visible: this.form.controls.visible.value,
      genres: this.trackGenres,
      duration: this.track.duration
    };
    if (this.trackId > 0) {
      this.trackService
        .updateTrack(trackData, true)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(_ => {
          this.loading = false;
          this.bsModalRef.hide();

          this.trackService.trackGenresUpdated.emit();
        });
    } else if(this.copyMode) {
      this.trackService
        .copyLightShowToLibTrack(trackData)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(_ => {
          this.loading = false;
          this.bsModalRef.hide();

          this.trackService.trackGenresUpdated.emit();
        });
    } else if (this.form.controls.reencode.value){
        const trackKey = "librarytracks/" + this.trackFile.name.toLowerCase().replace(/[^0-9a-zA-Z.]+/g, '-');
        const reencodedUrl = Environment.s3CdnUrl + "/" + trackKey;
        this.s3Service.uploadFile(this.trackFile, trackKey, 'get-reencoding-signed-s3-url')
          .pipe(mergeMap(_ => {
            return this.trackService.checkForReencodedFile(reencodedUrl)
              .pipe(mergeMap(res => {
                    if (res){
                      trackData.trackUrl = reencodedUrl;
                      return this.trackService.uploadTrack(this.trackFile, true, false)
                        .pipe(
                          takeUntil(this.ngOnDestroy$),
                          map(track => {
                            trackData['trackId'] = track.trackId;
                            return this.trackService.updateTrack(trackData, true);
                        }));
                    }
              }));
          })).subscribe(success => {
            this.loading = false;
            this.bsModalRef.hide();
            this.trackService.trackGenresUpdated.emit();
          }, error => {
            this.dialogService.alert('Uh oh!', `We tried to reencode that audio file to a constant bitrate but something failed. Please try again in a few minutes.`, this.viewRef);
            this.bsModalRef.hide();
          });
    } else {
      this.trackService
        .uploadTrack(this.trackFile, true)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(track => {
          trackData['trackId'] = track.trackId;
          this.trackService
            .updateTrack(trackData, true)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(updatedTrack => {
              this.loading = false;
              this.bsModalRef.hide();

              this.trackService.trackGenresUpdated.emit();
            });
        });
    }
  }

  trackFileChanged(files: FileList) {
    if (files.length > 0) {
      const track = files.item(0);
      this.uploadedTrackName = track.name.toLowerCase();
      this.trackFile = track;

      this.form.get('trackFile').setValue(track);
    }
  }

  toggleTrackVisible() {
    this.isTrackVisible = !this.isTrackVisible;
    this.form.controls.visible.patchValue(this.isTrackVisible);
  }


  lightShowJSONValidator(lightshow: string) {
    // const MINIMUM_SEGMENT_POSITION = 50;
    let parsedObj;
    try {
      parsedObj = JSON.parse(lightshow);
    } catch (e) {
      return false;
    }

    return parsedObj.filter(
      flash => !Object.values(FlashTypes).includes(+flash.type)).length === 0;
  }

  private _initializeForm(track) {
    this.track = track;
    this.trackGenres = track.genres;
    this.form = this.fb.group({
      title: [track.title, Validators.required],
      artist: [track.artist, Validators.required],
      notes: [track.notes],
      lightShow: [track.lightShow, [
        ((): ValidatorFn => {
          return (control: AbstractControl): { [key: string]: any } | null => {
            let isValid;
            if (control.value !== null) {
              isValid = this.lightShowJSONValidator(control.value);
            }
            return isValid
              ? null
              : { 'lightShow': { value: control.value } };
          };
        })()
      ]],
      trackFile: [this.trackFile, this.trackId !== -1 || this.copyMode ? null : Validators.required],
      visible: [track.visible],
      genres: [track.genres],
      trackUrl: [track.trackUrl],
      reencode: [track.reencode]
    });

    this.isTrackVisible = track.visible;
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
